import { useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation } from 'react-router-dom';
import {
  PermissionEnum,
  SchemaFilterType,
  useGetList,
  useQueryState,
} from '@ui/src';
import { schema } from '../variables/expired';
import {
  useDeleteCustomerDocumentMutation,
  useLazyGetCustomerOrganizationDocumentsStatsQuery,
  useLazyGetExpiredDocumentsQuery,
} from '../../../customers/store/services/customers';
import { CustomerDocument } from '../types';
import { OrganizationType } from '../../types';
import formatExpiredDocumentsList from '../helpers/format-expired-documents-list';

import ROUTES from '../../../../routes';

const useExpiredDocuments = ({
  customerId,
  setDeletePopupVisible,
  deleteDocumentIsSuccess,
  expiredDocuments,
  activePermissions,
  type,
  setSubMenuVisible,
  setSelectedSubMenuDocumentId,
  setRenamePopupVisible,
  setSelectedDocumentToRename,
  getFile,
  subMenuVisible,
  selectedSubMenuDocumentId,
  setExpiredDocumentsVisible,
}: {
  customerId?: string;
  setDeletePopupVisible: (data: boolean) => void;
  deleteDocumentIsSuccess?: boolean;
  expiredDocuments?: CustomerDocument[];
  type: OrganizationType;
  activePermissions?: PermissionEnum[];
  setSubMenuVisible: (data: boolean) => void;
  setSelectedSubMenuDocumentId: (data: string) => void;
  setRenamePopupVisible: (data: boolean) => void;
  setSelectedDocumentToRename: (data?: CustomerDocument) => void;
  getFile: (data: CustomerDocument) => void;
  subMenuVisible?: boolean;
  selectedSubMenuDocumentId?: string;
  setExpiredDocumentsVisible: (data: boolean) => void;
}) => {
  const [
    deleteExpiredDocumentPopupVisible,
    setDeleteExpiredDocumentPopupVisible,
  ] = useState(false);
  const [selectedDeleteDocument, setSelectedDeleteDocument] = useState<
    CustomerDocument | undefined
  >(undefined);

  const { search } = useLocation();

  const {
    filters,
    sort: expiredSort,
    onSortChange,
    onFilterChange,
  } = useQueryState(schema);

  const [
    getExpiredDocuments,
    {
      isLoading: expiredDocumentsIsLoading,
      isFetching: expiredDocumentsIsFetching,
    },
  ] = useLazyGetExpiredDocumentsQuery();
  const [getStats] = useLazyGetCustomerOrganizationDocumentsStatsQuery();
  const [
    deleteFile,
    { isSuccess: deleteSuccess, isLoading: deleteExpiredDocumentInProgress },
  ] = useDeleteCustomerDocumentMutation();

  const { getListAction, setPage, errorList } = useGetList<CustomerDocument>({
    getList: getExpiredDocuments,
    onFilterChange,
    onSortChange,
    itemId: customerId,
    schema,
  });

  const onDeleteExpitedDocument = async () => {
    const response = await deleteFile({
      id: customerId as string,
      documentId: selectedDeleteDocument?.id as string,
    });

    if (response) {
      setDeletePopupVisible(false);

      getStats(customerId as string);
    }
  };

  const formattedExpiredDocumentsData = useMemo(() => {
    const nextExpiredDocuments = expiredDocuments?.map((item) => {
      const result = { ...item };

      let documentLink;

      if (type === OrganizationType.INDIVIDUAL) {
        documentLink = generatePath(
          ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS_INFO,
          {
            customerId: customerId as string,
            documentId: item.id,
          },
        );
      } else {
        documentLink = generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS_INFO, {
          customerId: customerId as string,
          documentId: item.id,
        });
      }

      result.url = documentLink;

      return result;
    });

    const documentRenameAvailable =
      type === OrganizationType.INDIVIDUAL
        ? activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
          )
        : activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
          );

    const data = formatExpiredDocumentsList(
      nextExpiredDocuments as CustomerDocument[],
      setSubMenuVisible,
      setSelectedSubMenuDocumentId,
      setRenamePopupVisible,
      setSelectedDocumentToRename,
      getFile,
      setDeleteExpiredDocumentPopupVisible,
      setSelectedDeleteDocument,
      type,
      subMenuVisible,
      selectedSubMenuDocumentId,
      documentRenameAvailable,
      activePermissions,
      search,
    );

    return data;
  }, [
    expiredDocuments,
    subMenuVisible,
    setSubMenuVisible,
    selectedSubMenuDocumentId,
    setSelectedSubMenuDocumentId,
    search,
    type,
    activePermissions,
  ]);

  useEffect(() => {
    if (deleteDocumentIsSuccess) {
      getListAction({
        filters: {
          ...filters,
          take: {
            value: ['20'],
            type: SchemaFilterType.STRING,
          },
          status: {
            value: ['EXPIRED'],
            type: SchemaFilterType.STRING,
          },
        },
        sort: expiredSort,
      });
    }
  }, [deleteDocumentIsSuccess]);

  useEffect(() => {
    if (deleteSuccess) {
      setDeleteExpiredDocumentPopupVisible(false);
      setSelectedDeleteDocument(undefined);

      getListAction({
        filters: {
          ...filters,
          take: {
            value: ['20'],
            type: SchemaFilterType.STRING,
          },
          status: {
            value: ['EXPIRED'],
            type: SchemaFilterType.STRING,
          },
        },
        sort: expiredSort,
      });
    }
  }, [deleteSuccess]);

  useEffect(() => {
    if (customerId) {
      getListAction({
        filters: {
          ...filters,
          take: {
            value: ['20'],
            type: SchemaFilterType.STRING,
          },
          status: {
            value: ['EXPIRED'],
            type: SchemaFilterType.STRING,
          },
        },
        sort: expiredSort,
      });
    }
  }, [customerId]);

  return {
    setExpiredDocumentsVisible,
    setPage,
    filters,
    formattedExpiredDocumentsData,
    expiredSort,
    expiredDocumentsIsFetching,
    expiredDocumentsIsLoading,
    errorList,
    deleteExpiredDocumentPopupVisible,
    selectedDeleteDocument,
    setDeleteExpiredDocumentPopupVisible,
    onDeleteExpitedDocument,
    deleteExpiredDocumentInProgress,
    getListAction,
  };
};

export default useExpiredDocuments;
